import React, { useState } from "react";
import { ChevronDown, ExternalLink } from "lucide-react";
import UniversityRepeatableField from "../../Frontend/components/UniversityRepeatableField";

function CandidateCV({
  showCV,
  cvUrl,
  coverLetter,
  applicationFields = [],
  coverLetterEnabled = false,
}) {
  const [expandedJsonSections, setExpandedJsonSections] = useState({});

  const toggleJsonSection = (fieldIndex) => {
    setExpandedJsonSections((prev) => ({
      ...prev,
      [fieldIndex]: !prev[fieldIndex],
    }));
  };

  const getFileViewerUrl = (url) => {
    if (!url) return null;

    // If it's a PDF, return the original URL
    if (url.toLowerCase().endsWith(".pdf")) {
      return url;
    }

    // For DOC/DOCX files, use Microsoft Office Online Viewer to prevent automatic download
    if (
      url.toLowerCase().endsWith(".doc") ||
      url.toLowerCase().endsWith(".docx")
    ) {
      return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
    }

    // For other file types, return the original URL
    return url;
  };

  const renderFieldValue = (field, fieldIndex) => {
    // Handle university_repeatable field type
    if (field.field_type === "university_repeatable") {
      return (
        <UniversityRepeatableField
          fieldIndex={0}
          field={{ label: field.field_name }}
          isRequired={false}
          displayMode={true}
          initialValue={field.field_value}
        />
      );
    }

    // Handle JSON or array field values
    try {
      const parsedValue = JSON.parse(field.field_value);
      if (Array.isArray(parsedValue)) {
        return (
          <div className="w-full">
            {/* JSON Accordion Header */}
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleJsonSection(fieldIndex)}
                className="w-full flex items-center justify-between p-2 text-sm bg-muted hover:bg-muted transition-colors"
              >
                <span className="text-primary-dark font-semibold">
                  Degree Details
                </span>
                <ChevronDown
                  className={`w-3 h-3 transition-transform ${
                    expandedJsonSections[fieldIndex]
                      ? "transform rotate-180"
                      : ""
                  }`}
                />
              </button>
              {expandedJsonSections[fieldIndex] && (
                <div className="p-3 bg-card">
                  <div className="space-y-2 w-full">
                    {parsedValue.map((value, idx) => (
                      <div
                        key={idx}
                        className="flex gap-2 flex-col border border-border rounded-lg p-2"
                      >
                        {typeof value === "object" && value !== null ? (
                          Object.entries(value)
                            .filter(([key, val]) => key !== "id")
                            .map(([key, val]) => (
                              <div key={key} className="block w-full">
                                <label className=" text-primary-dark font-semibold capitalize">
                                  {key}
                                </label>
                                <p className="bg-muted text-primary-dark px-3 py-1 rounded-xl text-xs border border-border capitalize">
                                  {val || "N/A"}
                                </p>
                              </div>
                            ))
                        ) : (
                          <span className="text-primary-dark">
                            {value || "N/A"}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }
      if (typeof parsedValue === "object" && parsedValue !== null) {
        return (
          <div className="w-full">
            {/* JSON Accordion Header */}
            <div className="border border-border rounded-lg overflow-hidden">
              <button
                type="button"
                onClick={() => toggleJsonSection(fieldIndex)}
                className="w-full flex items-center justify-between p-2 bg-muted hover:bg-muted transition-colors"
              >
                <span className="text-primary-dark font-medium">
                  JSON Data ({Object.keys(parsedValue).length} fields)
                </span>
                <ChevronDown
                  className={`w-3 h-3 transition-transform ${
                    expandedJsonSections[fieldIndex]
                      ? "transform rotate-180"
                      : ""
                  }`}
                />
              </button>
              {expandedJsonSections[fieldIndex] && (
                <div className="p-3 bg-card">
                  <div className="space-y-2">
                    {Object.entries(parsedValue).map(([key, value], idx) => (
                      <div key={idx} className="flex gap-2">
                        <span className="font-medium text-primary-dark">
                          {key}:
                        </span>
                        <span className="text-primary-dark">
                          {value || "N/A"}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      }
    } catch (e) {
      // Not valid JSON, continue with normal rendering
    }

    // Handle text fields with rich content
    if (
      field.field_type === "text" ||
      field.field_type === "textarea" ||
      field.field_type === "wysiwyg"
    ) {
      return (
        <div className="bg-muted text-primary-dark px-3 py-1 rounded-xl text-xs border border-border inline-block">
          <div dangerouslySetInnerHTML={{ __html: field.field_value }}></div>
        </div>
      );
    }

    // Handle regular text fields (split by comma)
    if (field.field_type === "text") {
      return field.field_value.split(",").map((value, idx) => (
        <div
          key={idx}
          className="bg-muted text-primary-dark px-3 py-1 rounded-xl text-xs border border-border inline-block"
        >
          {value.trim()}
        </div>
      ));
    }

    // Handle other field types
    return (
      <div className="bg-muted text-primary-dark px-3 py-1 rounded-xl text-xs border border-border inline-block">
        {field.field_value || "N/A"}
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {showCV && cvUrl ? (
        <iframe
          src={getFileViewerUrl(cvUrl)}
          className="text-foreground w-full h-[800px] border-0"
          title="Candidate CV"
        />
      ) : (
        <>
          {coverLetterEnabled && coverLetter && (
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-primary-dark">
                Cover Letter
              </h3>
              {/^https?:\/\/[^\s]+$/.test(coverLetter) ? (
                // ✅ If it's a valid link, show it as a link
                <a
                  href={coverLetter}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 underline"
                  title="Opens in a new tab"
                >
                  Download Cover Letter
                  <ExternalLink className="w-4 h-4 " />
                </a>
              ) : (
                // ❌ Otherwise, show it as HTML content
                <div
                  className="text-primary-dark bg-muted border border-border p-4 rounded-lg"
                  dangerouslySetInnerHTML={{ __html: coverLetter }}
                />
              )}
            </div>
          )}

          {applicationFields?.map((field, index) => {
            if (field.field_type === "text" && field.form_type !== "personal") {
              return (
                <div className="space-y-4" key={index}>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-primary-dark">
                      {field.field_name}
                    </h3>
                    {renderFieldValue(field, index)}
                  </div>
                </div>
              );
            }

            // Handle university_repeatable fields
            if (field.field_type === "university_repeatable") {
              return (
                <div className="space-y-4" key={index}>
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold text-primary-dark">
                      {field.field_name}
                    </h3>
                    <div className="flex gap-2 flex-wrap">
                      {renderFieldValue(field, index)}
                    </div>
                  </div>
                </div>
              );
            }

            return null;
          })}
        </>
      )}
    </div>
  );
}

export default CandidateCV;
